import type { CalendarEventType, DailySegment, LeaveType } from '@fas/types';
import { minutesBetween, parseTimeOfDay, zonedDateTimeToUtc } from './time';

/*
 * Institutional calendar and leave (§23, §24, §63). Decides, deterministically, whether a
 * holiday, suspension or approved leave cancels a session or shortens a work segment.
 * "A class cancelled by an official suspension is never an absence" and "Holiday: no
 * absence unless duty explicitly scheduled".
 */

export interface CalendarEventFacts {
  id: string;
  eventType: CalendarEventType;
  title: string;
  /** Inclusive calendar dates (institution time zone). */
  startDate: string;
  endDate: string;
  /** Partial-day events (single date only): effective from `startTime` until `endTime` or midnight. */
  startTime: string | null;
  endTime: string | null;
  campusId: string | null;
  collegeId: string | null;
  departmentId: string | null;
  suspendsClasses: boolean;
  suspendsWork: boolean;
}

export interface LeaveFacts {
  id: string;
  leaveType: LeaveType;
  startDate: string;
  endDate: string;
  /** FULL_DAY or a half day (AM / PM) on a single date. */
  portion: DailySegment;
}

/**
 * What kind of session is being evaluated:
 * - REGULAR_CLASS: a meeting of a weekly class schedule
 * - ARRANGED_CLASS: a one-off class explicitly arranged for the date (makeup, substitute)
 * - DUTY: an approved overload / extra-load assignment or administrative duty
 * - DAILY: a full-time daily attendance segment
 */
export type CalendarSessionKind = 'REGULAR_CLASS' | 'ARRANGED_CLASS' | 'DUTY' | 'DAILY';

export interface CalendarSessionFacts {
  kind: CalendarSessionKind;
  /** Attendance date (the date the session starts). */
  date: string;
  start: Date;
  end: Date;
  campusId: string | null;
  collegeId: string | null;
  departmentId: string | null;
  /** Daily attendance segment (AM / PM / FULL_DAY). */
  segment?: DailySegment | null;
}

export const HOLIDAY_EVENT_TYPES: readonly CalendarEventType[] = ['NATIONAL_HOLIDAY', 'LOCAL_HOLIDAY', 'SCHOOL_HOLIDAY'];
/** Emergency suspensions override explicit arrangements; holidays and planned activities do not. */
export const SUSPENSION_EVENT_TYPES: readonly CalendarEventType[] = ['CLASS_SUSPENSION', 'WORK_SUSPENSION'];

export function isHolidayEvent(type: CalendarEventType): boolean {
  return HOLIDAY_EVENT_TYPES.includes(type);
}

const EVENT_LABEL: Record<CalendarEventType, string> = {
  NATIONAL_HOLIDAY: 'Holiday',
  LOCAL_HOLIDAY: 'Local holiday',
  SCHOOL_HOLIDAY: 'School holiday',
  CLASS_SUSPENSION: 'Classes suspended',
  WORK_SUSPENSION: 'Work suspended',
  FACULTY_DEVELOPMENT: 'Faculty development',
  INSTITUTIONAL_ACTIVITY: 'Institutional activity',
  OTHER: 'Calendar event',
};

/** Short label used in session remarks, e.g. "Classes suspended: Typhoon Signal No. 3". */
export function calendarEventLabel(type: CalendarEventType): string {
  return EVENT_LABEL[type];
}

/** Default effect of an event type; administrators may change the switches per event. */
export function defaultEventEffects(type: CalendarEventType): { suspendsClasses: boolean; suspendsWork: boolean } {
  switch (type) {
    case 'NATIONAL_HOLIDAY':
    case 'LOCAL_HOLIDAY':
    case 'SCHOOL_HOLIDAY':
    case 'WORK_SUSPENSION':
      return { suspendsClasses: true, suspendsWork: true };
    case 'CLASS_SUSPENSION':
    case 'FACULTY_DEVELOPMENT':
      return { suspendsClasses: true, suspendsWork: false };
    default:
      return { suspendsClasses: false, suspendsWork: false };
  }
}

/** Whether the event's organizational scope (none = institution-wide) covers the placement. */
export function eventCoversPlacement(e: Pick<CalendarEventFacts, 'campusId' | 'collegeId' | 'departmentId'>, p: Pick<CalendarSessionFacts, 'campusId' | 'collegeId' | 'departmentId'>): boolean {
  if (e.departmentId) return e.departmentId === p.departmentId;
  if (e.collegeId) return e.collegeId === p.collegeId;
  if (e.campusId) return e.campusId === p.campusId;
  return true;
}

export function eventCoversDate(e: Pick<CalendarEventFacts, 'startDate' | 'endDate'>, date: string): boolean {
  return e.startDate <= date && date <= e.endDate;
}

/** UTC window of an event on one of its dates (the whole local day unless times are set). */
export function eventWindow(e: Pick<CalendarEventFacts, 'startTime' | 'endTime'>, date: string, timeZone: string): { from: Date; to: Date } {
  const from = zonedDateTimeToUtc(date, e.startTime ? parseTimeOfDay(e.startTime) : 0, timeZone);
  const to = zonedDateTimeToUtc(date, e.endTime ? parseTimeOfDay(e.endTime) : 1440, timeZone);
  return { from, to };
}

/** Whether the event, by type and switches, stops this kind of session (§24, §63). */
export function eventStopsKind(e: Pick<CalendarEventFacts, 'eventType' | 'suspendsClasses' | 'suspendsWork'>, kind: CalendarSessionKind): boolean {
  switch (kind) {
    case 'REGULAR_CLASS':
      return e.suspendsClasses;
    case 'DAILY':
      return e.suspendsWork;
    // Explicitly arranged classes and duties still take place on holidays and planned
    // activities; only emergency suspensions stop them.
    case 'ARRANGED_CLASS':
      return SUSPENSION_EVENT_TYPES.includes(e.eventType) && (e.suspendsClasses || e.suspendsWork);
    case 'DUTY':
      return e.eventType === 'WORK_SUSPENSION' && e.suspendsWork;
  }
}

export interface CalendarEffect {
  event: CalendarEventFacts;
  /** SUSPENDED: the session does not take place. TRUNCATED: a daily segment ends early. */
  kind: 'SUSPENDED' | 'TRUNCATED';
  /** New end of a truncated daily segment. */
  endsAt?: Date;
  holiday: boolean;
}

/**
 * Effect of the calendar on one session. A session is suspended when it starts inside an
 * event's window; a daily segment already running when a partial-day suspension begins is
 * shortened to end at that moment. Whole-day events win over partial ones; ties go to the
 * earliest-created event (UUIDv7 ids sort by creation).
 */
export function calendarEffect(events: CalendarEventFacts[], session: CalendarSessionFacts, timeZone: string): CalendarEffect | null {
  const candidates = events
    .filter((e) => eventCoversDate(e, session.date) && eventCoversPlacement(e, session) && eventStopsKind(e, session.kind))
    .sort((a, b) => Number(!!a.startTime) - Number(!!b.startTime) || a.id.localeCompare(b.id));
  let truncation: CalendarEffect | null = null;
  for (const e of candidates) {
    const w = eventWindow(e, session.date, timeZone);
    if (session.start >= w.from && session.start < w.to) return { event: e, kind: 'SUSPENDED', holiday: isHolidayEvent(e.eventType) };
    if (session.kind === 'DAILY' && !truncation && session.start < w.from && w.from < session.end) {
      truncation = { event: e, kind: 'TRUNCATED', endsAt: w.from, holiday: isHolidayEvent(e.eventType) };
    }
  }
  return truncation;
}

export interface LeaveEffect {
  leave: LeaveFacts;
  /** EXCUSED: the session is cancelled. TRUNCATED: a daily segment starts later or ends earlier. */
  kind: 'EXCUSED' | 'TRUNCATED';
  startsAt?: Date;
  endsAt?: Date;
}

export function leaveCoversDate(l: Pick<LeaveFacts, 'startDate' | 'endDate'>, date: string): boolean {
  return l.startDate <= date && date <= l.endDate;
}

/**
 * Effect of approved leave on one session (§23). Full-day leave excuses every session of the
 * date. A half-day leave excuses the matching AM / PM segment and the classes on that half
 * of the day; a single-shift workday is shortened to the other half. `boundary` is the
 * instant separating the halves (the shift midpoint, or noon for classes).
 */
export function leaveEffect(leaves: LeaveFacts[], session: CalendarSessionFacts, boundary: Date): LeaveEffect | null {
  const covering = leaves.filter((l) => leaveCoversDate(l, session.date)).sort((a, b) => Number(a.portion !== 'FULL_DAY') - Number(b.portion !== 'FULL_DAY') || a.id.localeCompare(b.id));
  for (const l of covering) {
    if (l.portion === 'FULL_DAY') return { leave: l, kind: 'EXCUSED' };
    const morning = l.portion === 'AM';
    if (session.kind === 'DAILY' && session.segment && session.segment !== 'FULL_DAY') {
      if (session.segment === l.portion) return { leave: l, kind: 'EXCUSED' };
      continue;
    }
    if (session.kind === 'DAILY') {
      if (boundary <= session.start || boundary >= session.end) {
        // The whole segment lies in one half of the day.
        if (morning === session.start < boundary) return { leave: l, kind: 'EXCUSED' };
        continue;
      }
      return morning ? { leave: l, kind: 'TRUNCATED', startsAt: boundary } : { leave: l, kind: 'TRUNCATED', endsAt: boundary };
    }
    if (morning === session.start < boundary) return { leave: l, kind: 'EXCUSED' };
  }
  return null;
}

/** Midpoint of a window, floored to the minute (the half-day boundary of a single shift). */
export function midpoint(start: Date, end: Date): Date {
  return new Date(start.getTime() + Math.floor(minutesBetween(start, end) / 2) * 60_000);
}

/** Required minutes scaled to a shortened window (at least 0, whole minutes). */
export function scaleRequiredMinutes(requiredMinutes: number, originalWindow: number, newWindow: number): number {
  if (originalWindow <= 0) return 0;
  return Math.max(0, Math.round((requiredMinutes * Math.max(0, newWindow)) / originalWindow));
}
